import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Button, Container, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AccountContext } from "../../store/AccountContext";
import { v4FlatWinsAtBreakpointsSx } from "../../theme/v4CompatSx";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * SUBSCRIPTIONS ESCALATION CLASS — the post-trial "join now" upsell wall.
 * Styling-only: no handler, condition, state value, prop or DOM node moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1) — declared but never referenced from
 * the LIVE JSX: `username`, `bodyText`, `bottomHeader`, `bottomText`,
 * `countdown`, `countdownContainer`, `countdownWrapper`,
 * `countdownLabelWrapper`, `countdownUnitWrapper`, `countdownUnitFirstWrapper`
 * and `countdownUnitLabel`. The last six were only ever used by the
 * commented-out `countdownRenderer` block below, which is left untouched; if
 * that block is ever revived the rules are recoverable from git history for
 * this commit. The `<div className={classes.box}>` referenced a key that does
 * not exist in this file's `useStyles` object, so it resolved to
 * `className={undefined}` and applied nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().background — a bare <div>. The literal
// `@media (min-width:600px)` / `(min-width:960px)` become
// `breakpoints.up("sm"|"md")`; `themeV5` pins those to the v4 values
// (600/960), so the emitted query is identical.
const Background = styled("div")(({ theme }) => ({
  backgroundImage: `url(https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/freetrial_bg.jpg)`,
  boxShadow: "inset 4000px 0 0 0 rgba(8, 5, 5, 0.8)",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundAttachment: "scroll",
  width: "100%",
  height: "75vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [theme.breakpoints.up("sm")]: {
    height: "500px",
  },
  [theme.breakpoints.up("md")]: {
    height: "550px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

// was: useStyles().header
const headerSx = (theme) => ({
  color: theme.palette.common.white,
  fontSize: "3.2rem",
  lineHeight: "2.8rem",
  textAlign: "left",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  marginBottom: theme.spacing(1),
  "& span": {
    fontWeight: 600,
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3.6rem",
    lineHeight: "3.6rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "4.6rem",
    lineHeight: "4.4rem",
  },
});

// was: useStyles().subHeader
const subHeaderSx = (theme) => ({
  color: "#bbb",
  fontSize: "1.3rem",
  lineHeight: "1.4rem",
  fontWeight: "500",
  textAlign: "left",
  width: "90%",
  textShadow: "-2px 2px 2px #171717",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.3rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
  },
});

// was: useStyles().joinNowButton, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default — and this theme's `primary.main` IS the lime that
// `typography.button` paints the button background with, so an uncompensated
// port renders an invisible lime-on-lime "Join Now" button.
//
// PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
// own breakpoint rule"): `fontSize` has to be re-stated at `up("sm")` and
// `up("md")`. This Button is `size="large"`, and `themeV5`'s
// `MuiButton.styleOverrides.sizeLarge` sets its own `fontSize` at both
// breakpoints; in v9 those @media blocks are emitted AFTER this class's flat
// declarations, so above 600px the label grew off the theme's large-button
// size instead of staying at v4's 1.2rem. In v4 the makeStyles sheet had a
// later JSS index and won at every width.
const joinNowButtonSx = (theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      fontSize: "1.2rem",
      textAlign: "left",
      margin: theme.spacing(1),
      marginBottom: theme.spacing(2),
      fontWeight: 500,
      textDecoration: "none",
      "&:hover": {
        cursor: "pointer",
      },
      [theme.breakpoints.up("sm")]: {
        textAlign: "center",
      },
      color: "text.primary",
    },
    ["fontSize"],
    ["sm", "md"],
  );

export default function TrialSubscriptionWindow() {
  const { userProfile } = useContext(AccountContext);

  // const countdownRenderer = ({ days, hours, minutes, seconds, completed }) => {
  //   if (completed) {
  //     validateLogin();
  //     return null;
  //   } else {
  //     // Render countdown
  //     return (
  //       <Container className={classes.countdownContainer}>
  //         <Grid container className={classes.countdownWrapper}>
  //           {days >= 1 && (
  //             <Grid
  //               item
  //               xs={3}
  //               sm={3}
  //               md={3}
  //               className={classes.countdownUnitWrapper}
  //             >
  //               <span>
  //                 {days.toString().length === 1 ? "0" : ""}
  //                 {days}
  //               </span>
  //             </Grid>
  //           )}
  //           <Grid
  //             item
  //             xs={3}
  //             sm={3}
  //             md={3}
  //             className={classes.countdownUnitWrapper}
  //           >
  //             <span>
  //               {hours.toString().length === 1 ? "0" : ""}
  //               {hours}
  //             </span>
  //           </Grid>
  //           <Grid
  //             item
  //             xs={3}
  //             sm={3}
  //             md={3}
  //             className={classes.countdownUnitWrapper}
  //           >
  //             <span>
  //               {minutes.toString().length === 1 ? "0" : ""}
  //               {minutes}
  //             </span>
  //           </Grid>
  //           <Grid
  //             item
  //             xs={3}
  //             sm={3}
  //             md={3}
  //             className={classes.countdownUnitWrapper}
  //           >
  //             <span>
  //               {seconds.toString().length === 1 ? "0" : ""}
  //               {seconds}
  //             </span>
  //           </Grid>
  //         </Grid>
  //         <Grid container className={classes.countdownLabelWrapper}>
  //           {days >= 1 && (
  //             <Grid
  //               item
  //               xs={3}
  //               sm={3}
  //               md={3}
  //               className={classes.countdownUnitLabel}
  //             >
  //               Day{days > 1 ? "s" : ""}
  //             </Grid>
  //           )}
  //           <Grid
  //             item
  //             xs={3}
  //             sm={3}
  //             md={3}
  //             className={classes.countdownUnitLabel}
  //           >
  //             Hours
  //           </Grid>
  //           <Grid
  //             item
  //             xs={3}
  //             sm={3}
  //             md={3}
  //             className={classes.countdownUnitLabel}
  //           >
  //             Mins
  //           </Grid>
  //           <Grid
  //             item
  //             xs={3}
  //             sm={3}
  //             md={3}
  //             className={classes.countdownUnitLabel}
  //           >
  //             Secs
  //           </Grid>
  //         </Grid>
  //       </Container>
  //     );
  //   }
  // };

  return (
    <div>
      {userProfile && (
        <>
          <Background>
            <Container fixedcomponent="main" maxWidth="lg">
              <div>
                <Grid container sx={v4GridContainerWidthSx}>
                  <Grid size={{ xs: 12, sm: 10 }}>
                    <Typography component="h1" variant="h1" sx={headerSx}>
                      Enjoyed Your Trial?
                    </Typography>
                    <Typography component="h2" variant="h2" sx={subHeaderSx}>
                      Want More? Join Now in Two Simple Steps.
                    </Typography>
                    {/* <Typography
                      component="h2"
                      variant="h2"
                      className={classes.subHeader}
                    >
                      {userProfile.firstName}, your free trial is now over and
                      this means it’s time to decide if you’re onboard or not!
                    </Typography> */}

                    {/* <Typography variant="body1" className={classes.bottomText}>
                      FarmFLiX has a Locked window for the months of February
                      through November. We&apos;re only Open for New members
                      once a year BUT because you&apos;ve only just discovered
                      us Your Luck is in... You&apos;ve got 24 hours to decide
                      if you want join us on this years journey to have access
                      to all the new weekly episodes before your account is
                      Locked until Black Friday!
                    </Typography>
                    <Typography
                      component="h2"
                      variant="h2"
                      className={classes.subHeader}
                    >
                      <Hidden xsDown>
                        The clock is ticking... Don&apos;t Miss Out!
                      </Hidden> */}

                    {/* Visible XS only */}
                    {/* <Hidden smUp>
                        The clock is ticking...
                        <br />
                        Don&apos;t Miss Out!
                      </Hidden>
                    </Typography> */}
                    {/* <Countdown
                      date={Date.parse(userProfile.subscriptionExpiryDate)}
                      renderer={countdownRenderer}
                      zeroPadTime={2}
                      daysInHours={true}
                    /> */}
                    <Link
                      to="/myaccount/join-now"
                      style={{ textDecoration: "none" }}
                    >
                      <Button size="large" sx={joinNowButtonSx}>
                        Join Now
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </div>
            </Container>
          </Background>
        </>
      )}
    </div>
  );
}
